/**
 * Unified Ink Service
 * Manages all written content across the platform:
 * - Knowledge articles
 * - PDF documents (from ink app)
 * - Markdown notes
 * - Social media posts (future)
 * - AI-generated content (future)
 *
 * Single source of truth for all "ink" (written content) in the organization
 */
import { supabase } from '../lib/supabase-client';
export const inkService = {
    /**
     * Create new ink document
     */
    async create(data) {
        try {
            const { data: document, error } = await supabase
                .from('ink')
                .insert([data])
                .select()
                .single();
            if (error)
                throw error;
            return document;
        }
        catch (error) {
            console.error('Error creating ink document:', error);
            return null;
        }
    },
    /**
     * Get ink document by ID
     */
    async getById(id) {
        try {
            const { data, error } = await supabase
                .from('ink')
                .select('*')
                .eq('id', id)
                .single();
            if (error)
                throw error;
            return data;
        }
        catch (error) {
            console.error('Error fetching ink document:', error);
            return null;
        }
    },
    /**
     * Get all ink documents for organization
     */
    async getByOrganization(organizationId, query) {
        try {
            let qry = supabase
                .from('ink')
                .select('*')
                .eq('organization_id', organizationId)
                .is('deleted_at', null);
            // Apply filters
            if (query?.filter) {
                if (query.filter.contentType) {
                    qry = qry.eq('content_type', query.filter.contentType);
                }
                if (query.filter.category) {
                    qry = qry.eq('category', query.filter.category);
                }
                if (query.filter.isPublished !== undefined) {
                    qry = qry.eq('is_published', query.filter.isPublished);
                }
                if (query.filter.sourceType) {
                    qry = qry.eq('source_type', query.filter.sourceType);
                }
                if (query.filter.processingStatus) {
                    qry = qry.eq('processing_status', query.filter.processingStatus);
                }
            }
            // Apply ordering
            const orderBy = query?.orderBy || 'created_at';
            const orderDirection = query?.orderDirection || 'desc';
            qry = qry.order(orderBy, { ascending: orderDirection === 'asc' });
            // Apply pagination
            if (query?.limit) {
                qry = qry.limit(query.limit);
            }
            if (query?.offset) {
                qry = qry.range(query.offset, query.offset + (query.limit || 50) - 1);
            }
            const { data, error } = await qry;
            if (error)
                throw error;
            return data || [];
        }
        catch (error) {
            console.error('Error fetching organization ink documents:', error);
            return [];
        }
    },
    /**
     * Get knowledge articles (content_type = 'knowledge_article')
     */
    async getArticles(organizationId, category) {
        try {
            let qry = supabase
                .from('ink')
                .select('*')
                .eq('organization_id', organizationId)
                .eq('content_type', 'knowledge_article')
                .eq('is_published', true)
                .is('deleted_at', null)
                .order('created_at', { ascending: false });
            if (category) {
                qry = qry.eq('category', category);
            }
            const { data, error } = await qry;
            if (error)
                throw error;
            return data || [];
        }
        catch (error) {
            console.error('Error fetching knowledge articles:', error);
            return [];
        }
    },
    /**
     * Get PDFs (content_type = 'pdf')
     */
    async getPDFs(organizationId) {
        try {
            const { data, error } = await supabase
                .from('ink')
                .select('*')
                .eq('organization_id', organizationId)
                .eq('content_type', 'pdf')
                .is('deleted_at', null)
                .order('created_at', { ascending: false });
            if (error)
                throw error;
            return data || [];
        }
        catch (error) {
            console.error('Error fetching PDFs:', error);
            return [];
        }
    },
    /**
     * Search ink documents by title or content
     */
    async search(organizationId, query, contentType) {
        try {
            let qry = supabase
                .from('ink')
                .select('*')
                .eq('organization_id', organizationId)
                .is('deleted_at', null);
            // Use or operator to search both title and content
            qry = qry.or(`title.ilike.%${query}%,content.ilike.%${query}%`);
            if (contentType) {
                qry = qry.eq('content_type', contentType);
            }
            const { data, error } = await qry.order('updated_at', { ascending: false });
            if (error)
                throw error;
            return data || [];
        }
        catch (error) {
            console.error('Error searching ink documents:', error);
            return [];
        }
    },
    /**
     * Get categories for organization
     */
    async getCategories(organizationId, contentType) {
        try {
            let qry = supabase
                .from('ink')
                .select('category')
                .eq('organization_id', organizationId)
                .is('deleted_at', null)
                .not('category', 'is', null);
            if (contentType) {
                qry = qry.eq('content_type', contentType);
            }
            const { data, error } = await qry;
            if (error)
                throw error;
            // Extract unique categories
            const categories = new Set();
            (data || []).forEach((item) => {
                if (item.category) {
                    categories.add(item.category);
                }
            });
            return Array.from(categories).sort();
        }
        catch (error) {
            console.error('Error fetching categories:', error);
            return [];
        }
    },
    /**
     * Update ink document
     */
    async update(id, updates) {
        try {
            const { data, error } = await supabase
                .from('ink')
                .update(updates)
                .eq('id', id)
                .select()
                .single();
            if (error)
                throw error;
            return data;
        }
        catch (error) {
            console.error('Error updating ink document:', error);
            return null;
        }
    },
    /**
     * Publish ink document
     */
    async publish(id) {
        try {
            const { data, error } = await supabase
                .from('ink')
                .update({
                is_published: true,
                publish_date: new Date().toISOString(),
                processing_status: 'published'
            })
                .eq('id', id)
                .select()
                .single();
            if (error)
                throw error;
            return data;
        }
        catch (error) {
            console.error('Error publishing ink document:', error);
            return null;
        }
    },
    /**
     * Soft delete ink document
     */
    async delete(id) {
        try {
            const { error } = await supabase
                .from('ink')
                .update({ deleted_at: new Date().toISOString() })
                .eq('id', id);
            if (error)
                throw error;
            return true;
        }
        catch (error) {
            console.error('Error deleting ink document:', error);
            return false;
        }
    },
    /**
     * Permanently delete ink document (hard delete)
     */
    async permanentlyDelete(id) {
        try {
            const { error } = await supabase
                .from('ink')
                .delete()
                .eq('id', id);
            if (error)
                throw error;
            return true;
        }
        catch (error) {
            console.error('Error permanently deleting ink document:', error);
            return false;
        }
    },
    /**
     * Increment view count
     */
    async incrementViews(id) {
        try {
            const { error } = await supabase
                .from('ink')
                .update({ views: supabase.rpc('increment_ink_views', { ink_id: id }) })
                .eq('id', id);
            if (error) {
                // Fallback: fetch, increment, update
                const doc = await this.getById(id);
                if (doc) {
                    await this.update(id, { views: (doc.views || 0) + 1 });
                }
            }
            return true;
        }
        catch (error) {
            console.error('Error incrementing view count:', error);
            return false;
        }
    },
    /**
     * Create new version of document (for versioning)
     */
    async createVersion(id, updates) {
        try {
            // Get current document
            const current = await this.getById(id);
            if (!current)
                return null;
            // Update current document version
            await this.update(id, { version: current.version + 1 });
            // Create new version record
            const newVersion = await this.create({
                ...updates,
                organization_id: current.organization_id,
                owner_id: current.owner_id,
                previous_version_id: id,
                version: current.version + 1,
                content_type: current.content_type
            });
            return newVersion;
        }
        catch (error) {
            console.error('Error creating document version:', error);
            return null;
        }
    },
    /**
     * Get document version history
     */
    async getVersionHistory(id) {
        try {
            const versions = [];
            let currentId = id;
            while (currentId) {
                const doc = await this.getById(currentId);
                if (!doc)
                    break;
                versions.push(doc);
                currentId = doc.previous_version_id;
            }
            return versions;
        }
        catch (error) {
            console.error('Error fetching version history:', error);
            return [];
        }
    },
    /**
     * Get all documents by type (document, article, form, etc.)
     */
    async getDocumentsByType(organizationId, docType) {
        try {
            const { data, error } = await supabase
                .from('ink')
                .select('*')
                .eq('organization_id', organizationId)
                .eq('document_type', docType)
                .is('deleted_at', null)
                .order('edited_at', { ascending: false });
            if (error)
                throw error;
            return data || [];
        }
        catch (error) {
            console.error(`Error fetching ${docType} documents:`, error);
            return [];
        }
    },
    /**
     * Get documents in workspace with folder hierarchy
     */
    async getWorkspaceDocuments(workspaceId) {
        try {
            const { data, error } = await supabase
                .from('ink')
                .select('*')
                .eq('workspace_id', workspaceId)
                .eq('document_type', 'document')
                .is('deleted_at', null)
                .order('edited_at', { ascending: false });
            if (error)
                throw error;
            return data || [];
        }
        catch (error) {
            console.error('Error fetching workspace documents:', error);
            return [];
        }
    },
    /**
     * Get user's personal documents (all types, excluding shared)
     */
    async getUserDocuments(organizationId, userId) {
        try {
            const { data, error } = await supabase
                .from('ink')
                .select('*')
                .eq('organization_id', organizationId)
                .eq('owner_id', userId)
                .is('deleted_at', null)
                .order('edited_at', { ascending: false });
            if (error)
                throw error;
            return data || [];
        }
        catch (error) {
            console.error('Error fetching user documents:', error);
            return [];
        }
    },
    /**
     * Get shared documents (collaborating on or receiving)
     */
    async getSharedDocuments(organizationId, userId) {
        try {
            const { data, error } = await supabase
                .from('ink')
                .select('*')
                .eq('organization_id', organizationId)
                .contains('collaborators', [userId])
                .is('deleted_at', null)
                .order('edited_at', { ascending: false });
            if (error)
                throw error;
            return data || [];
        }
        catch (error) {
            console.error('Error fetching shared documents:', error);
            return [];
        }
    },
    /**
     * Update document blocks (for InkApp)
     */
    async updateBlocks(id, blocks, blocksOrder) {
        try {
            const { data, error } = await supabase
                .from('ink')
                .update({
                blocks,
                blocks_order: blocksOrder,
                block_count: blocks.length,
                edited_at: new Date().toISOString()
            })
                .eq('id', id)
                .select()
                .single();
            if (error)
                throw error;
            return data;
        }
        catch (error) {
            console.error('Error updating document blocks:', error);
            return null;
        }
    },
    /**
     * Add collaborator to document
     */
    async addCollaborator(id, userId, role = 'editor') {
        try {
            const doc = await this.getById(id);
            if (!doc)
                return null;
            const collaborators = new Set(doc.collaborators || []);
            collaborators.add(userId);
            const sharedWith = doc.shared_with || [];
            if (!sharedWith.find((item) => item.userId === userId)) {
                sharedWith.push({
                    userId,
                    role,
                    sharedAt: new Date().toISOString()
                });
            }
            const { data, error } = await supabase
                .from('ink')
                .update({
                collaborators: Array.from(collaborators),
                shared_with: sharedWith,
                is_shared: true
            })
                .eq('id', id)
                .select()
                .single();
            if (error)
                throw error;
            return data;
        }
        catch (error) {
            console.error('Error adding collaborator:', error);
            return null;
        }
    },
    /**
     * Remove collaborator from document
     */
    async removeCollaborator(id, userId) {
        try {
            const doc = await this.getById(id);
            if (!doc)
                return null;
            const collaborators = new Set(doc.collaborators || []);
            collaborators.delete(userId);
            const sharedWith = (doc.shared_with || []).filter((item) => item.userId !== userId);
            const { data, error } = await supabase
                .from('ink')
                .update({
                collaborators: Array.from(collaborators),
                shared_with: sharedWith,
                is_shared: collaborators.size > 0
            })
                .eq('id', id)
                .select()
                .single();
            if (error)
                throw error;
            return data;
        }
        catch (error) {
            console.error('Error removing collaborator:', error);
            return null;
        }
    },
    /**
     * Log activity for document (edit, publish, share, etc.)
     */
    async logActivity(documentId, action, blockId, details) {
        try {
            const { error } = await supabase
                .from('ink_activity')
                .insert([{
                    document_id: documentId,
                    action,
                    actor_id: (await supabase.auth.getUser()).data.user?.id,
                    block_id: blockId,
                    details,
                    created_at: new Date().toISOString()
                }]);
            if (error)
                throw error;
            return true;
        }
        catch (error) {
            console.error('Error logging activity:', error);
            return false;
        }
    },
    /**
     * Create article (convenience method - document_type='article')
     */
    async createArticle(organizationId, title, content, category) {
        const user = (await supabase.auth.getUser()).data.user;
        if (!user)
            return null;
        return this.create({
            organization_id: organizationId,
            owner_id: user.id,
            title,
            content,
            document_type: 'article',
            category,
            source_type: 'manual_write',
            status: 'draft',
            created_by: user.id,
            updated_by: user.id
        });
    },
    /**
     * Create ink document with blocks (convenience method - document_type='document')
     */
    async createInkDocument(organizationId, title, workspaceId) {
        const user = (await supabase.auth.getUser()).data.user;
        if (!user)
            return null;
        return this.create({
            organization_id: organizationId,
            owner_id: user.id,
            workspace_id: workspaceId,
            title,
            document_type: 'document',
            blocks: [],
            blocks_order: [],
            source_type: 'manual_write',
            status: 'draft',
            created_by: user.id,
            updated_by: user.id
        });
    }
};
